const _ = require("lodash");
const ShopBundleData = require("./data/dbShopBundle.json");
const CartItemsUtil = require("./CartItemsUtil");
const {SubscriptionSkuEnum, BUNDLE_GRADE_PREFIX} = require("./StoreSkuUtil");

// NOTE: Many of these functions are coded with the assumption that the non-
// bundleID options on bundle items, with the sole exception of the
// subscription need a displayName option, are inconsequential. If this changes, we
// may need to update these.

const {YEARLY_SUBSCRIPTION_SKU, SUBSCRIPTION_CODE_SKU} = SubscriptionSkuEnum;

// Minimum number of books in a grade set that would trigger bundle suggestion.
const MIN_BOOKS_TO_BUNDLE = 4;

const getBundleList = () => _.keys(ShopBundleData);
const getBundleInfo = bundleID => ShopBundleData[bundleID];
const bundleGradeList = _.sortBy(_.map(ShopBundleData, "gradeNumber"));
const getBundleGradeList = () => bundleGradeList;

const getBundleGrade = bundleID => {
	return getBundleInfo(bundleID)?.gradeNumber;
};

// For matching item lists with expected bundles, we turn all skus (other than
// the subscription) into a sorted semicolon separated string that we can do
// === checks on. Here, we precompute these strings for ShopBundleData.
const cachedBundleSkuStrings = _.mapValues(ShopBundleData, info => {
	const skus = info.skus.filter(
		sku => sku !== YEARLY_SUBSCRIPTION_SKU && sku !== SUBSCRIPTION_CODE_SKU
	);
	return _.sortBy(skus).join(";");
});

// Given array of cart items, get the array of names for the yearly subscription
// items, bundled or not. If the items have quantity greater than 1, that many
// copies of the displayName are returned, unless noStack is set to true.
const getSubscriptionNames = function(items, noStack = false) {
	return _.flatMap(items, i => {
		if (i.sku !== YEARLY_SUBSCRIPTION_SKU) return [];
		const quantity = noStack ? 1 : i.quantity;
		return _.times(quantity, () => i.options.displayName);
	});
};

// Internal validateItems helper.
const validateOneBundleID = function(items, bundleID) {
	if (!bundleID) return true;
	if (!items.length) return false;
	// Get the total number of subscriptions
	let quantity;
	if (bundleID.includes(SUBSCRIPTION_CODE_SKU)) {
		quantity = items.filter(i => i.sku === SUBSCRIPTION_CODE_SKU)[0].quantity;
	} else {
		quantity = getSubscriptionNames(items).length;
	}
	const noSubItems = items.filter(
		i => i.sku !== YEARLY_SUBSCRIPTION_SKU && i.sku !== SUBSCRIPTION_CODE_SKU
	);
	if (!noSubItems.every(i => i.quantity === quantity)) return false;
	const skuString = _.sortBy(noSubItems.map(i => i.sku)).join(";");
	return cachedBundleSkuStrings[bundleID] === skuString;
};

// Returns one item.
const makeSubscription = function(grade, displayName) {
	const bundleID = `${BUNDLE_GRADE_PREFIX}${grade}`;
	return {
		sku: YEARLY_SUBSCRIPTION_SKU,
		quantity: 1,
		options: {displayName, bundleID},
	};
};

// Returns array of eight items, all books.
const makeGradeSet = function(grade, quantity = 1, options = {}) {
	const {isCodeBundle} = options;
	let bundleID = `${BUNDLE_GRADE_PREFIX}${grade}`;
	if (isCodeBundle) {
		bundleID = `${bundleID}-${SUBSCRIPTION_CODE_SKU}`;
	}
	const itemOptions = options.unbundled ? {} : {bundleID};
	let books = ShopBundleData[bundleID].skus;
	books = books.filter(
		sku => sku !== YEARLY_SUBSCRIPTION_SKU && sku !== SUBSCRIPTION_CODE_SKU
	);
	return books.map(sku => ({
		sku,
		quantity,
		options: itemOptions,
	}));
};

const makeGradeSetWithBundleID = function(bundleID, quantity, options = {}) {
	const bundleGrade = getBundleGrade(bundleID);
	const isCodeBundle = bundleID.includes(SUBSCRIPTION_CODE_SKU);
	return makeGradeSet(bundleGrade, quantity, {...options, isCodeBundle});
};

const getAllBundleItems = function(bundleID, quantity, displayName) {
	const allBooksInBundle = makeGradeSetWithBundleID(bundleID, quantity);
	let subscriptionItem;
	if (bundleID.includes(SUBSCRIPTION_CODE_SKU)) {
		subscriptionItem = {
			sku: SUBSCRIPTION_CODE_SKU,
			quantity: quantity,
			options: {bundleID},
		};
	} else {
		subscriptionItem = {
			sku: YEARLY_SUBSCRIPTION_SKU,
			quantity: quantity,
			options: {bundleID},
		};
	}
	const codeBundle = [subscriptionItem, ...allBooksInBundle];
	return codeBundle;
};

// Returns array of two items, all books.
const makeScienceGradeSet = function(grade, quantity = 1, options = {}) {
	const bundleID = `bundle-science-grade-${grade}`;
	const itemOptions = options.unbundled ? {} : {bundleID};
	const books = ShopBundleData[bundleID].skus.filter(
		sku => sku !== YEARLY_SUBSCRIPTION_SKU
	);
	return books.map(sku => ({
		sku,
		quantity,
		options: itemOptions,
	}));
};

// Returns array of nine items, combining previous two functions.
const makeItems = function(grade, displayName) {
	return [makeSubscription(grade, displayName)].concat(makeGradeSet(grade));
};

const validateItems = function(cartItems) {
	// Need || "" to ensure that a falsy bundle ID stays falsy when it gets
	// converted to an object key (a string).
	const byBundle = _.groupBy(cartItems, i => i.options.bundleID || "");
	return _.every(byBundle, validateOneBundleID);
};

// Returns same array object if valid, else removes all invalid bundles and
// returns that array.
const fixItems = function(cartItems) {
	// Need || "" to ensure that a falsy bundle ID stays falsy when it gets
	// converted to an object key (a string).
	const byBundle = _.groupBy(cartItems, i => i.options.bundleID || "");
	const validBundleIDTable = _.mapValues(byBundle, validateOneBundleID);
	if (_.every(validBundleIDTable)) return cartItems;
	return cartItems.filter(x => validBundleIDTable[x.options.bundleID]);
};

// Yearly bundle subscriptions collapse by student name, but deferred and
// immediate bundles must stay separate even when they share a displayName.
const getYearlyBundleCollapseKey = function(item) {
	const displayName = item.options.displayName;
	if (!displayName) return "";
	if (item.options.bookLevelDeferred) {
		const grade = item.options.provisionalGrade ?? "";
		return `${displayName}\0deferred\0${grade}`;
	}
	return `${displayName}\0immediate`;
};

// Returns the same cart items array except that for any subscription name with
// more than one bundle attached, all those subscription-yearly items are
// replaced with one with options {displayName, bundleIDs: [...]}
const getItemsCollapsedBundles = function(cartItems, options) {
	const {skipValidation = false} = options || {};
	if (!skipValidation && !validateItems(cartItems)) {
		throw new Error(
			"Passed cartItems with invalid bundles to getItemsWithBundles"
		);
	}
	// All unnamed, nonbundle items are grouped under "".
	// This object will contain all the bundled subscription and subscription
	// codes, keyed by the displayName of the subscription or the bundleID for a
	// subscription code.
	const bySubscriptionKey = _.groupBy(cartItems, i => {
		// a single displayName should only have a single bundle or subscription
		// associated with it
		if (i.sku === YEARLY_SUBSCRIPTION_SKU && i.options.bundleID)
			return getYearlyBundleCollapseKey(i);
		// there should only be at most one code bundle type in the cart
		// for example, a cart can contain
		// 1 instance of "grade-bundle-3-beast-bulk-codes" and
		// 1 instance of "grade-bundle-4-beast-bulk-codes"
		else if (i.sku === SUBSCRIPTION_CODE_SKU && i.options.bundleID)
			return i.options.bundleID;
		return "";
	});
	const newItems = bySubscriptionKey[""] || [];
	if (newItems.length === cartItems.length) return cartItems;
	// For each subscription key that is not "", there should only be one item
	// associated with that key. items is an array since there was a time when a
	// displayName could be associated with multiple bundles.
	_.each(bySubscriptionKey, (items, subscriptionKey) => {
		if (!subscriptionKey) return;
		const subItem = items.find(
			i => i.sku === YEARLY_SUBSCRIPTION_SKU || i.sku === SUBSCRIPTION_CODE_SKU
		);
		const quantity = subItem?.quantity || 1;
		const bundleIDs = items.map(i => i.options.bundleID);
		if (subscriptionKey.includes(SUBSCRIPTION_CODE_SKU)) {
			newItems.push({
				sku: SUBSCRIPTION_CODE_SKU,
				quantity,
				// no display name for bulk codes
				options: {bundleIDs},
			});
		} else {
			// Carry forward any deferment flags from the raw subscription so the
			// collapsed item can round-trip through undo (re-add) without losing them.
			const deferredOpts = {};
			if (subItem && subItem.options.bookLevelDeferred) {
				deferredOpts.bookLevelDeferred = true;
			}
			if (subItem && subItem.options.provisionalGrade != null) {
				deferredOpts.provisionalGrade = subItem.options.provisionalGrade;
			}
			newItems.push({
				sku: YEARLY_SUBSCRIPTION_SKU,
				quantity,
				options: {
					displayName: subItem.options.displayName,
					bundleIDs,
					...deferredOpts,
				},
			});
		}
	});
	return newItems;
};

// Pass cartItems and its value when passed to getItemsCollapsedBundles. This
// returns collapsedItems with each item changed to an array of indices, giving
// which items in cartItems it came from. The array of indices is always a
// singleton except for subscriptions that are in multiple bundles.
const getItemsCollapsedBundlesMapping = function(cartItems, collapsedItems) {
	// The nonbundle items in cartItems appear identically in collapsedItems; we
	// take advantage of this by using them as keys in a map. Bundled items use
	// the displayName string as their key.
	const indexMap = new Map();
	cartItems.forEach((item, i) => {
		if (item.sku === YEARLY_SUBSCRIPTION_SKU && item.options.bundleID) {
			const key = getYearlyBundleCollapseKey(item);
			const orig = indexMap.get(key);
			indexMap.set(key, (orig || []).concat([i]));
		} else if (item.sku === SUBSCRIPTION_CODE_SKU && item.options.bundleID) {
			// key by bundleID for subscription codes, since the object structure
			// changes from bundleID -> bundleIDs from cartItems to collapsedItems.
			// A cart should only have one unique instance of a subscription code for
			// a particular bundle.
			const orig = indexMap.get(item.options.bundleID);
			indexMap.set(item.options.bundleID, (orig || []).concat([i]));
		} else {
			indexMap.set(item, [i]);
		}
	});
	return collapsedItems.map(item => {
		if (item.sku === YEARLY_SUBSCRIPTION_SKU && item.options.bundleIDs) {
			return indexMap.get(getYearlyBundleCollapseKey(item));
		} else if (
			item.sku === SUBSCRIPTION_CODE_SKU &&
			item.options.bundleIDs &&
			item.options.bundleIDs.length > 0
		) {
			return indexMap.get(item.options.bundleIDs[0]);
		} else {
			return indexMap.get(item);
		}
	});
};

// Gets an array of bundle IDs present in the cart, possibly with repeats.
const getBundleIDs = function(cartItems) {
	return cartItems
		.filter(i => i.sku === YEARLY_SUBSCRIPTION_SKU && i.options.bundleID)
		.map(i => i.options.bundleID);
};

const getUnbundledItems = function(cartItems) {
	return cartItems.filter(i => !i.options.bundleID);
};

// Collapses each bundled subscription into a single item with sku "bundle" and
// options {isBundle: true, displayName, bundleIDs: [...]}. Items not in a
// bundle are left unchanged.
// Example of a collapsed item: {
//   sku: "bundle",
//   quantity: 1,
//   options: {
//     isBundle: true,
//     displayName: "Grogg",
//     bundleIDs: ["bundle-grade-3", "bundle-grade-4"]
//   },
// }
const getItemsWithBundles = function(cartItems, options) {
	const {skipValidation = false} = options || {};
	if (!skipValidation && !validateItems(cartItems)) {
		throw new Error(
			"Passed cartItems with invalid bundles to getItemsWithBundles"
		);
	}
	// collapsed will hold the new "subscription" items with the bundleIDs option
	// (not bundleID).
	const collapsed = getItemsCollapsedBundles(cartItems, {skipValidation});
	return _.flatMap(collapsed, item => {
		// First check for the non-subscription item in a bundle. Remove them.
		if (item.options.bundleID) return [];
		// Let unbundled items pass through.
		if (
			(item.sku !== YEARLY_SUBSCRIPTION_SKU &&
				item.sku !== SUBSCRIPTION_CODE_SKU) ||
			!item.options.bundleIDs
		) {
			return [item];
		}
		// Else, this must be a collapsed subscription with a bundleIDs option.
		const bundleIDs = item.options.bundleIDs.sort();
		// displayName is only present if the bundle is NOT a code bundle
		const displayName = item.options.displayName;
		const deferredBundleOpts = {};
		if (item.options.bookLevelDeferred) {
			deferredBundleOpts.bookLevelDeferred = true;
		}
		if (item.options.provisionalGrade != null) {
			deferredBundleOpts.provisionalGrade = item.options.provisionalGrade;
		}
		const collapsedBundle = {
			sku: "bundle",
			quantity: item.quantity,
			options: {
				// include a displayName only if it exists
				...(displayName ? {displayName} : {}),
				bundleIDs,
				isBundle: true,
				...deferredBundleOpts,
			},
			subitems: bundleIDs.flatMap(bundleID => {
				return getAllBundleItems(bundleID, item.quantity);
			}),
		};
		return [collapsedBundle];
	});
};

// If passed all the singlar items of a bundle (with or without subscription,
// i.e. for upgrades), condenses it into one bundle item with subscription and
// books as sub-items.
// Example of a collapsed item: {
//   sku: "bundle",
//   quantity: 1,
//   options: {
//     isBundle: true,
//     displayName: "Grogg",
//     bundleID: ["bundle-grade-3"]
//   },
//   subscription: subscription item,
//   books: [book items]
// }
const getUpgradeItemsWithBundles = function(cartItems) {
	if (_.isEmpty(cartItems)) return [];
	if (!_.every(cartItems, i => _.get(i, "options.bundleID"))) return cartItems;

	const subscriptionItem = _.find(
		cartItems,
		i => i.sku === YEARLY_SUBSCRIPTION_SKU
	);
	const bundleID = _.get(cartItems[0], "options.bundleID");
	const bundleItem = {
		sku: "bundle",
		quantity: 1,
		options: {
			displayName: _.get(subscriptionItem, "options.displayName", ""),
			bundleIDs: [bundleID],
			isBundle: true,
		},
	};
	bundleItem.subscription = subscriptionItem;
	bundleItem.books = _.without(cartItems, subscriptionItem);
	return [bundleItem];
};

// Returns an array of student names attached to unbundled yearly
// subscriptions. Can also take a getItemsWithBundles return.
const getUnbundledSubscriptions = function(cartItems) {
	const unbundledItems = getUnbundledItems(cartItems);
	return getSubscriptionNames(unbundledItems, true);
};

const getUnbundledSubscriptionCodes = function(cartItems) {
	const unbundledItems = getUnbundledItems(cartItems);
	// Only 1 item with this sku should be found. If there are multiple, it will
	// be in the "quantity" field of this first object.
	return unbundledItems.filter(i => i.sku === SUBSCRIPTION_CODE_SKU);
};

// Returns an array of bundle IDs whose book sets are in the cart and
// unbundled.
const getUnbundledBookSets = function(cartItems) {
	const unbundledItems = getUnbundledItems(cartItems);
	const unbundledItemsBySku = _.keyBy(unbundledItems, i => i.sku);
	// Returns true for non-book items or skus that are in the cart unbundled.
	const isSkuOK = sku =>
		sku === YEARLY_SUBSCRIPTION_SKU ||
		sku === SUBSCRIPTION_CODE_SKU ||
		unbundledItemsBySku[sku];
	// "looping" through each bundle to see if all its books (+ subscription) are
	// in the cart and unbundled
	const presentBookSets = _.pickBy(ShopBundleData, info =>
		info.skus.every(isSkuOK)
	);
	return _.keys(presentBookSets);
};

// Returns an array with how many unique books are in the cart for a particular
// grade set
const getOrderedUnbundledBooks = function(cartItems) {
	const unbundledItems = getUnbundledItems(cartItems);
	const unbundledItemsBySku = _.map(unbundledItems, i => i.sku);

	let sets = _.map(ShopBundleData, (bundleInfo, key) => {
		const bundleBookSkus = bundleInfo.skus.filter(
			sku => sku !== YEARLY_SUBSCRIPTION_SKU && sku !== SUBSCRIPTION_CODE_SKU
		);
		const includedSkus = _.intersection(unbundledItemsBySku, bundleBookSkus);
		return {
			bundleID: key,
			gradeNumber: bundleInfo.gradeNumber,
			numIncludedSkus: includedSkus.length,
			missingSkus: _.without(bundleBookSkus, ...unbundledItemsBySku),
		};
	});
	sets = sets.filter(s => s.numIncludedSkus >= MIN_BOOKS_TO_BUNDLE);
	return (
		_.orderBy(sets, ["numIncludedSkus", "gradeNumber"], ["desc", "asc"]) || []
	);
};

// Gets all unbundled subscriptions, not just yearly ones.
const getOrderedUnbundledSubscriptions = function(cartItems) {
	const unbundledItems = getUnbundledItems(cartItems);
	const unbundledSubscriptions = _.filter(
		unbundledItems,
		i => i.options.displayName && !i.options.isBundle
	);
	// Order by yearly, then monthly (sku desc)
	// Order by display name alphabetically (displayName asc)
	return (
		_.orderBy(
			unbundledSubscriptions,
			["sku", "options.displayName"],
			["desc", "asc"]
		) || []
	);
};

// Returns true if there are possible bundle suggestions.
const canSuggestBundle = function(cartItems) {
	const subs = getOrderedUnbundledSubscriptions(cartItems);
	const books = getOrderedUnbundledBooks(cartItems);
	if (!books.length || !subs.length) return false;
	if (books[0].missingSkus.length) {
		return subs[0].sku === YEARLY_SUBSCRIPTION_SKU;
	} else {
		return !!subs.length;
	}
};

// Returns true if there are unbundled items that could be bundled.
// includeSubscriptionCodes: true will check the cart for subscription codes in
// addition to non-code subscriptions that can be bundled. If setting this to
// true, make sure to also set it to true in the <BundleConversionModal>
// component so that codes show up as a choice for conversion.
const canConvertBundle = function(cartItems, options = {}) {
	const {includeSubscriptionCodes = false} = options;
	// check that the cart includes (subscription OR subscription code) AND book set
	return (
		(getUnbundledSubscriptions(cartItems).length > 0 ||
			(includeSubscriptionCodes &&
				getUnbundledSubscriptionCodes(cartItems).length > 0)) &&
		getUnbundledBookSets(cartItems).length > 0
	);
};

// Internal helper for adding and removing bundles. The adjusted items are ones
// in a bundle, unless forUnbundled is true.
// extraSubOptions: additional options to merge onto the subscription item only
// (used to re-apply deferment flags when re-adding a deferred bundle via undo).
const adjustBundleItems = function(
	cartItems,
	subscriptionKey,
	bundleID,
	quantity,
	forUnbundled = false,
	extraSubOptions = {}
) {
	const baseOptions = forUnbundled ? {} : {bundleID};
	return ShopBundleData[bundleID].skus.reduce((is, sku) => {
		const options = _.clone(baseOptions);
		// for a yearly, use the subscriptionKey (student name) as the displayName
		if (sku === YEARLY_SUBSCRIPTION_SKU) {
			options.displayName = subscriptionKey;
			// When removing, look up the actual item so that any extra options
			// (e.g. bookLevelDeferred, provisionalGrade set for the deferment flow)
			// are preserved in the match. areItemsEqual uses _.isEqual, so a
			// constructed subset of options would never find items with extra keys.
			if (quantity < 0) {
				const actual = _.find(is, i => {
					if (i.sku !== YEARLY_SUBSCRIPTION_SKU) return false;
					if (_.get(i, "options.bundleID") !== bundleID) return false;
					if (_.get(i, "options.displayName") !== subscriptionKey) {
						return false;
					}
					const deferred = !!extraSubOptions.bookLevelDeferred;
					if (!!_.get(i, "options.bookLevelDeferred") !== deferred) {
						return false;
					}
					if (
						deferred &&
						extraSubOptions.provisionalGrade != null &&
						_.get(i, "options.provisionalGrade") !==
							extraSubOptions.provisionalGrade
					) {
						return false;
					}
					return true;
				});
				if (actual) {
					return CartItemsUtil.adjustItem(is, sku, quantity, actual.options);
				}
			} else {
				// When adding, apply any extra sub options (e.g. bookLevelDeferred).
				_.assign(options, extraSubOptions);
			}
		}
		return CartItemsUtil.adjustItem(is, sku, quantity, options);
	}, cartItems);
};

// Combines the subscriptionKey (which is a name for a montly/yearly and
// "beast-bulk-codes" for a bulk code) and bundle ID into a bundle. These parameters
// should be part of the returns for getUnbundledSubscriptions and
// getUnbundledBookSets. Returns the same items array if this conversion could
// not be done.
const convertBundle = function(cartItems, subscriptionKey, bundleID) {
	const hasSubscription =
		getUnbundledSubscriptions(cartItems).indexOf(subscriptionKey) > -1;
	const hasSubscriptionCode =
		getUnbundledSubscriptionCodes(cartItems).length > 0;
	const subIsOK = hasSubscription || hasSubscriptionCode;
	if (!subIsOK) return cartItems;
	const booksAreOK = getUnbundledBookSets(cartItems).indexOf(bundleID) > -1;
	if (!booksAreOK) return cartItems;
	// remove the unbundled books in the set and subscription
	let newItems = adjustBundleItems(
		cartItems,
		subscriptionKey,
		bundleID,
		-1,
		true
	);
	// add the bundled books and subscription
	newItems = adjustBundleItems(newItems, subscriptionKey, bundleID, 1);
	return newItems;
};

// Adds items to cart so that a full bundle can be created and then creates that
// bundle. Doesn't take subscription name or bundle ID because it chooses the
// automatically (takes the first one in availabe lists).
const convertSuggestedBundle = function(cartItems) {
	if (!canSuggestBundle(cartItems)) return cartItems;
	const subscription = getOrderedUnbundledSubscriptions(cartItems)[0];
	const books = getOrderedUnbundledBooks(cartItems)[0];
	const subscriptionName = subscription.options.displayName;
	const bundleID = books.bundleID;
	let newItems = adjustBundleItems(
		cartItems,
		subscriptionName,
		bundleID,
		-1,
		true
	);
	newItems = adjustBundleItems(newItems, subscriptionName, bundleID, 1);

	if (subscription.sku !== YEARLY_SUBSCRIPTION_SKU) {
		// Delete the extra monthly subscription that got turned into a yearly.
		newItems = CartItemsUtil.removeItem(newItems, subscription);
	}

	return newItems;
};

const addBundle = function(
	cartItems,
	displayName,
	bundleID,
	quantity = 1,
	extraSubOptions = {}
) {
	return adjustBundleItems(
		cartItems,
		displayName,
		bundleID,
		quantity,
		false,
		extraSubOptions
	);
};

// quantity is the number of bundles to remove. It should be a positive number.
const removeBundle = function(
	cartItems,
	displayName,
	bundleID,
	quantity = 1,
	extraSubOptions = {}
) {
	return adjustBundleItems(
		cartItems,
		displayName,
		bundleID,
		quantity * -1,
		false,
		extraSubOptions
	);
};

// Removes all bundles attached to the given displayName.
const removeNameBundles = function(cartItems, displayName) {
	const collapsed = getItemsCollapsedBundles(cartItems);
	const toRemove = _.find(collapsed, i => {
		return i.options.displayName === displayName && i.options.bundleIDs;
	});
	if (toRemove) {
		const reducer = (ci, bundleID) => removeBundle(ci, displayName, bundleID);
		cartItems = toRemove.options.bundleIDs.reduce(reducer, cartItems);
	}
	return cartItems;
};

// Get the subscription items associated with the bundle.
// cartItems is the original list of cart items (unbundled)
const getBundleSubscriptions = function(cartItems, bundleItem) {
	return _.filter(cartItems, item => {
		return item.options.displayName === bundleItem.options.displayName;
	});
};

// Converts all items for given bundleID to a different grade level
// This is probably not robust to changing item skus -
// but I'm pretty sure lots of other things might break if we do that.
const convertBundleLevel = function(newLevel, bundleID, items) {
	const newBundleID = `${BUNDLE_GRADE_PREFIX}${newLevel}`;
	const oldLevelString = _.last(bundleID);
	return _.map(items, i => {
		const bid = _.get(i, "options.bundleID");
		if (bid && bid !== newBundleID) {
			const newItem = _.cloneDeep(i);
			if (i.sku !== YEARLY_SUBSCRIPTION_SKU) {
				i.sku = _.replace(i.sku, oldLevelString, newLevel.toString());
			}
			i.options.bundleID = newBundleID;
			return newItem;
		} else {
			return i;
		}
	});
};

module.exports = {
	getBundleList,
	getBundleInfo,
	getBundleGradeList,
	getBundleGrade,
	getAllBundleItems,
	makeSubscription,
	convertBundleLevel,
	makeGradeSet,
	makeGradeSetWithBundleID,
	makeScienceGradeSet,
	makeItems,
	validateItems,
	fixItems,
	getItemsCollapsedBundles,
	getItemsCollapsedBundlesMapping,
	getItemsWithBundles,
	getBundleIDs,
	getUnbundledItems,
	getUnbundledSubscriptions,
	getUnbundledSubscriptionCodes,
	getUnbundledBookSets,
	canConvertBundle,
	getOrderedUnbundledSubscriptions,
	getOrderedUnbundledBooks,
	canSuggestBundle,
	convertBundle,
	convertSuggestedBundle,
	addBundle,
	removeBundle,
	getBundleSubscriptions,
	getUpgradeItemsWithBundles,
	removeNameBundles,
};
